import React, { useEffect, useState } from 'react';
import { ViewState } from '../../types';

import { Helmet } from 'react-helmet';
import { JsonLd } from '../JsonLd';
import { BookOpen, Scale, Search, Shield, Zap, Database, MessageSquare, Briefcase, FileSignature, Newspaper, Landmark, Users, Building2, Calculator, Home, Laptop, TreePine, Lightbulb, Book, Heart, Vote, Smartphone, Apple, ArrowDownCircle, Info } from 'lucide-react';
import { LEGAL_TOPICS } from '../../lib/topics';

const iconMap: Record<string, React.FC<any>> = {
  Landmark, Shield, Users, Building2, Calculator, Home, Briefcase, Laptop, TreePine, Lightbulb, Scale, Book, Heart, Vote
};

interface LandingViewProps {
  onGetStarted: () => void;
  onNavigate: (view: ViewState, customPath?: string) => void;
  user?: any;
}

const FeatureCard = ({ icon: Icon, title, description, colorClass }: { icon: any, title: string, description: string, colorClass: string }) => (
  <div className="p-8 rounded-[32px] bg-slate-50 border border-slate-100 hover:shadow-xl hover:shadow-slate-200/50 transition-all group">
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform ${colorClass}`}>
      <Icon className="w-7 h-7" />
    </div>
    <h3 className="text-xl font-bold text-slate-900 mb-3">{title}</h3>
    <p className="text-slate-600 leading-relaxed">{description}</p>
  </div>
);

const StatBox = ({ value, label, className = '', children }: { value: string, label: string, className?: string, children?: React.ReactNode }) => (
  <div className={`bg-slate-800 p-8 rounded-3xl border border-slate-700 text-center flex flex-col items-center ${className}`}>
    <div className="text-4xl font-black text-emerald-400 mb-2">{value}</div>
    <div className="text-slate-300 font-medium text-sm mb-4">{label}</div>
    {children}
  </div>
);

export function LandingView({ onGetStarted, onNavigate, user }: LandingViewProps) {
  const [showInstallModal, setShowInstallModal] = React.useState<'android' | 'ios' | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = React.useState(false);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Try local server endpoint first (fast, bypasses Firestore quota)
        const res = await fetch('/api/system/statistics');
        if (res.ok) {
          const data = await res.json();
          setStats(data);
          return;
        }
      } catch (apiErr) {
        console.warn('Failed to fetch statistics via local API, trying Firestore fallback...', apiErr);
      }

      // Fallback to client-side Firestore if local API fails
      try {
        const { db } = await import('../../firebase');
        const { doc, getDoc } = await import('firebase/firestore');
        const docRef = doc(db, 'system_metadata', 'statistics');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setStats(docSnap.data());
        }
      } catch (err) {
        console.error('Failed to fetch platform statistics from Firestore fallback:', err);
      }
    };
    fetchStats();
  }, []);

  const handleDownloadClick = (platform: 'android' | 'ios') => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    setShowInstallModal(platform);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans relative">
      <Helmet>
        <title>PakLawSite — #1 Pakistan Law Site | Case Laws, PLD &amp; Legal Database</title>
        <meta name="description" content="PakLawSite is Pakistan's #1 pakistan law site. Search Supreme Court PLD case laws, statutes, and find verified lawyers. The best pak law site for legal research online." />
        <meta name="keywords" content="pakistan law site, pak law site, pld law site, paklaw, law site, paklawsite, pak law, pld site, case laws online, supreme court pakistan" />
        <link rel="canonical" href="https://www.paklawsite.com/" />
      </Helmet>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          'name': 'PakLawSite',
          'alternateName': ['Pakistan Law Site', 'Pak Law Site', 'PLD Law Site', 'PakLaw', 'Law Site'],
          'url': 'https://www.paklawsite.com/',
          'description': 'Pakistan\'s #1 law site for PLD case laws, Supreme Court judgments, and comprehensive legal research.'
        }}
      />
      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] p-8 max-w-md w-full shadow-2xl border border-slate-100 text-center relative animate-in fade-in zoom-in duration-200">
            <button aria-label="Close modal" onClick={() => setShowLoginPrompt(false)} className="absolute top-6 right-6 p-2 rounded-full hover:bg-slate-100 text-slate-500">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-2">Authentication Required</h3>
            <p className="text-slate-500 mb-8">You must be logged in and have an active subscription to download the PakLawSite mobile applications.</p>
            <button onClick={() => onNavigate('login')} className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl shadow-lg transition-all active:scale-95">
              Log In or Register
            </button>
          </div>
        </div>
      )}

      {/* App Install Instructions Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] p-8 max-w-md w-full shadow-2xl border border-slate-100 text-center relative animate-in fade-in zoom-in duration-200">
            <button aria-label="Close modal" onClick={() => setShowInstallModal(null)} className="absolute top-6 right-6 p-2 rounded-full hover:bg-slate-100 text-slate-500">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="w-16 h-16 bg-slate-100 text-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6">
              {showInstallModal === 'ios' ? <Apple className="w-8 h-8" /> : <Smartphone className="w-8 h-8" />}
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-2">
              Install on {showInstallModal === 'ios' ? 'iPhone' : 'Android'}
            </h3>
            <p className="text-slate-500 mb-6 text-sm">
              Follow these simple steps to install PakLawSite directly to your home screen {showInstallModal === 'ios' ? 'without the App Store:' : 'instantly:'}
            </p>
            
            <div className="bg-slate-50 rounded-2xl p-5 text-left space-y-4 border border-slate-100 mb-6">
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs shrink-0">1</div>
                <p className="text-sm text-slate-700">Open <b>{showInstallModal === 'ios' ? 'Safari' : 'Google Chrome'}</b> on your phone and go to <i>paklawsite.com</i></p>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs shrink-0">2</div>
                <p className="text-sm text-slate-700">
                  {showInstallModal === 'ios' 
                    ? <>Tap the <b>Share</b> button at the bottom of the screen (the square with an arrow pointing up)</>
                    : <>Tap the <b>3-dot menu</b> icon at the top right corner of the screen</>}
                </p>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs shrink-0">3</div>
                <p className="text-sm text-slate-700">Tap <b>Add to Home Screen</b> in the list</p>
              </div>
            </div>
            
            <button onClick={() => setShowInstallModal(null)} className="w-full py-4 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-2xl shadow-lg transition-all active:scale-95">
              Got it, thanks!
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white overflow-hidden border-b border-slate-800">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/3 pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-32 relative z-10 flex flex-col items-center text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/80 border border-slate-700 backdrop-blur-sm mb-8 text-sm font-medium text-slate-300">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            PakLawSite Beta is Live
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black tracking-tight text-white mb-6 leading-tight max-w-4xl mx-auto">
            PakLawSite &mdash; Pakistan's Premier <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">AI Legal Assistant & Research Platform</span>
          </h1>
          
          <h2 className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-12 leading-relaxed font-normal">
            Get instant AI-powered legal advice for Pakistan law. Search Supreme Court judgments, access High Court precedents, draft legal documents, and find verified lawyers available 24/7.
          </h2>
          
          <div className="flex flex-wrap gap-4 items-center justify-center max-w-4xl mx-auto">
            <button
              onClick={onGetStarted}
              className="px-6 py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-2xl shadow-lg shadow-emerald-900/20 transition-all active:scale-95 flex items-center gap-2 text-lg"
            >
              <Search className="w-5 h-5" />
              Search Precedents
            </button>
            <button
              onClick={() => onNavigate('advocate-marketplace')}
              className="px-6 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-2xl border border-slate-700 transition-all active:scale-95 flex items-center gap-2 text-lg"
            >
              <Briefcase className="w-5 h-5" />
              Legal Experts
            </button>
            <button
              onClick={() => onNavigate('workspace')}
              className="px-6 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-2xl border border-slate-700 transition-all active:scale-95 flex items-center gap-2 text-lg"
            >
              <FileSignature className="w-5 h-5" />
              Drafting Workspace
            </button>
            <button
              onClick={() => onNavigate('blogs')}
              className="px-6 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-2xl border border-slate-700 transition-all active:scale-95 flex items-center gap-2 text-lg"
            >
              <Newspaper className="w-5 h-5" />
              Legal Blogs
            </button>
            <button
              onClick={() => onNavigate('laws')}
              className="px-6 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-2xl border border-slate-700 transition-all active:scale-95 flex items-center gap-2 text-lg"
            >
              <BookOpen className="w-5 h-5" />
              Laws and Acts
            </button>
          </div>
        </div>
      </section>

      {/* Platform Statistics Section */}
      {stats && (
        <section className="py-20 bg-slate-900 border-b border-slate-800 text-white relative">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-black mb-4">The Largest Legal Research Database in Pakistan</h2>
              <p className="text-slate-500 text-lg max-w-2xl mx-auto">Access millions of citations, case laws, and automated drafting templates instantly.</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <StatBox value={stats.totalLawyers?.toLocaleString() || '0'} label="Platform Lawyers">
                {stats.allLawyers && stats.allLawyers.length > 0 && (
                  <select aria-label="Select" 
                    className="w-full bg-slate-900 border border-slate-600 text-slate-300 text-xs rounded-xl px-3 py-2 outline-none focus:border-emerald-500 cursor-pointer"
                    onChange={(e) => {
                      if (e.target.value) {
                        onNavigate('advocate-marketplace', `/marketplace?advocateId=${e.target.value}`);
                      }
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>Select a Lawyer...</option>
                    {stats.allLawyers.slice(0, 50).map((lawyer: any) => (
                      <option key={lawyer.id} value={lawyer.slug || lawyer.id}>{lawyer.name}</option>
                    ))}
                  </select>
                )}
              </StatBox>
              <StatBox value={stats.totalPrecedents?.toLocaleString() || '0'} label="Judgments & Precedents" />
              <StatBox value={stats.totalJudges?.toLocaleString() || '0'} label="Honorable Judges">
                {stats.allJudges && stats.allJudges.length > 0 && (
                  <select aria-label="Select" 
                    className="w-full bg-slate-900 border border-slate-600 text-slate-300 text-xs rounded-xl px-3 py-2 outline-none focus:border-emerald-500 cursor-pointer"
                    onChange={(e) => {
                      if (e.target.value) {
                        onNavigate('search', `?judge=${encodeURIComponent(e.target.value)}`);
                      }
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>Filter by Judge...</option>
                    {stats.allJudges.slice(0, 50).map((judge: any) => (
                      <option key={judge.name} value={judge.name}>{judge.name} ({judge.count})</option>
                    ))}
                  </select>
                )}
              </StatBox>
              <StatBox value={stats.totalLaws?.toLocaleString() || '0'} label="Public Laws & Acts" />
            </div>

            {stats.topCategories && stats.topCategories.length > 0 && (
              <div className="bg-slate-800/50 p-8 rounded-3xl border border-slate-700 backdrop-blur-sm">
                <h3 className="text-xl font-bold text-center mb-6 text-slate-200">Top Precedent Categories</h3>
                <div className="flex flex-wrap justify-center gap-4">
                  {stats.topCategories.map((cat: any, idx: number) => (
                    <div key={idx} className="flex items-center gap-3 bg-slate-800 px-4 py-2.5 rounded-xl border border-slate-600">
                      <span className="font-medium text-slate-300">{cat.name}</span>
                      <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full text-xs font-bold">{cat.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Features Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">Powerful tools for modern legal practice.</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Stop digging through endless unindexed PDFs. Find exactly what you need in seconds with generative AI optimization.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard 
              icon={Database} 
              title="Structured Database" 
              description="Thousands of cases structurally indexed by facts, legal principles, laws discussed, and final outcomes. Search by exact intent, not just keywords." 
              colorClass="bg-emerald-100 text-emerald-600"
            />
            <FeatureCard 
              icon={Zap} 
              title="AI Assistant" 
              description="Ask complex legal questions in plain English or Urdu. The AI analyzes judgments to provide direct answers with precise citations." 
              colorClass="bg-blue-100 text-blue-600"
            />
            <FeatureCard 
              icon={Shield} 
              title="Verified Advocates" 
              description="A specialized marketplace connecting citizens with verified lawyers. Review chamber profiles, track records, and schedule consultations." 
              colorClass="bg-purple-100 text-purple-600"
            />
          </div>
        </div>
      </section>

      {/* Competitor Comparison Section */}
      <section className="py-24 bg-slate-50 border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">Why We Are Better Than Traditional Platforms</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">See how PakLawSite compares to traditional law digests and outdated search portals.</p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-3 bg-slate-900 text-white p-6 border-b border-slate-800">
              <div className="font-bold">Feature</div>
              <div className="font-bold text-center">Traditional Platforms</div>
              <div className="font-bold text-center text-emerald-400">PakLawSite</div>
            </div>
            
            <div className="grid grid-cols-3 p-6 border-b border-slate-100 items-center">
              <div className="text-slate-700 font-medium">Search Technology</div>
              <div className="text-slate-500 text-center text-sm">Exact Keyword Match (Hard to use)</div>
              <div className="text-emerald-600 text-center font-bold text-sm bg-emerald-50 py-2 rounded-lg">AI Semantic Search (Understands Intent)</div>
            </div>
            <div className="grid grid-cols-3 p-6 border-b border-slate-100 items-center bg-slate-50/50">
              <div className="text-slate-700 font-medium">Case Summaries</div>
              <div className="text-slate-500 text-center text-sm">Manual reading required (Hours)</div>
              <div className="text-emerald-600 text-center font-bold text-sm bg-emerald-50 py-2 rounded-lg">Instant AI Headnotes (Seconds)</div>
            </div>
            <div className="grid grid-cols-3 p-6 border-b border-slate-100 items-center">
              <div className="text-slate-700 font-medium">Urdu Translation</div>
              <div className="text-slate-500 text-center text-sm">None (English only)</div>
              <div className="text-emerald-600 text-center font-bold text-sm bg-emerald-50 py-2 rounded-lg">Full AI Urdu Translation</div>
            </div>
            <div className="grid grid-cols-3 p-6 items-center bg-slate-50/50">
              <div className="text-slate-700 font-medium">Drafting Tools</div>
              <div className="text-slate-500 text-center text-sm">Static Word Templates</div>
              <div className="text-emerald-600 text-center font-bold text-sm bg-emerald-50 py-2 rounded-lg">Automated AI Drafter</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Everything you need to know about Pakistan's top AI legal platform.</p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-xl font-bold text-slate-900 mb-3" itemProp="name">Is PakLawSite free to use?</h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-slate-600 leading-relaxed" itemProp="text">Yes! Searching case laws, exploring the database, and using basic AI features is entirely free. We also offer Premium plans for advanced high-volume drafting and deep legal analysis.</p>
              </div>
            </div>
            
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-xl font-bold text-slate-900 mb-3" itemProp="name">How accurate is the AI Legal Chatbot for Pakistan law?</h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-slate-600 leading-relaxed" itemProp="text">Highly accurate. Unlike generic AI, our chatbot is specifically fine-tuned on the Constitution of Pakistan, the Pakistan Penal Code (PPC), Civil Procedure Code (CPC), and thousands of verified Supreme Court precedents (PLD, SCMR).</p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-xl font-bold text-slate-900 mb-3" itemProp="name">Can I search and ask questions in Urdu?</h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-slate-600 leading-relaxed" itemProp="text">Yes, PakLawSite fully supports English, Urdu, and Roman Urdu. You can read complex legal case summaries translated into plain Urdu for better understanding.</p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-xl font-bold text-slate-900 mb-3" itemProp="name">Are the lawyers on this platform verified?</h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-slate-600 leading-relaxed" itemProp="text">Yes. Every lawyer listed in our Find Lawyer Pakistan directory is verified through their respective Bar Council registry to ensure they are actively licensed to practice.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Topics Section */}
      <section className="py-24 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-[#0e2a47] mb-6">Explore Legal Topic Hubs</h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Dive deep into specialized areas of Pakistani law. Our curated topic hubs aggregate landmark precedents, essential statutes, and expert analysis into comprehensive research centers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {LEGAL_TOPICS.map((topic) => {
              const Icon = iconMap[topic.icon] || BookOpen;
              return (
                <div 
                  key={topic.id}
                  onClick={() => onNavigate('topic-hub', `/topics/${topic.slug}`)}
                  className="group cursor-pointer p-6 rounded-2xl bg-slate-50 border border-slate-200 hover:border-blue-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-700 transition-colors">{topic.title}</h3>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    {topic.description}
                  </p>
                  <div className="flex items-center text-sm font-bold text-blue-600">
                    Explore Topic &rarr;
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Download App Section */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[100px] translate-x-1/2 -translate-y-1/2 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[100px] -translate-x-1/2 translate-y-1/2 pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 flex flex-col items-center text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 mb-6 shadow-xl">
            <Smartphone className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black mb-6 tracking-tight">Take PakLawSite Everywhere.</h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-12 leading-relaxed">
            Download our dedicated mobile applications for Android and iOS. Access the legal database, draft documents, and chat with the AI assistant natively on your device.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center gap-6 justify-center">
            <button
              onClick={() => handleDownloadClick('android')}
              className="w-full sm:w-auto px-8 py-5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-2xl shadow-lg shadow-emerald-900/30 transition-all active:scale-95 flex items-center justify-center gap-3 text-lg"
            >
              <Smartphone className="w-6 h-6" />
              <div className="text-left leading-tight">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-200">Install on</div>
                <div>Android</div>
              </div>
              <Info className="w-5 h-5 ml-2 opacity-50" />
            </button>

            <button
              onClick={() => handleDownloadClick('ios')}
              className="w-full sm:w-auto px-8 py-5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white font-bold rounded-2xl shadow-lg transition-all active:scale-95 flex items-center justify-center gap-3 text-lg"
            >
              <Apple className="w-6 h-6" />
              <div className="text-left leading-tight">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Install on</div>
                <div>iPhone (iOS)</div>
              </div>
              <Info className="w-5 h-5 ml-2 opacity-50" />
            </button>
          </div>
          
          <p className="mt-8 text-sm text-slate-500 font-medium flex items-center justify-center gap-2">
            <Shield className="w-4 h-4" /> Downloads restricted to registered members only.
          </p>
        </div>
      </section>

      {/* Trust & Stats Section */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-black mb-6 leading-tight">Built for speed.<br/>Designed for accuracy.</h2>
              <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                We're mapping the entire ecosystem of Pakistani jurisprudence. By extracting the core ratio decidendi, arguments, and timelines from raw text, we deliver actionable intelligence instantly.
              </p>
              <ul className="space-y-4">
                {[
                  'Daily updates from the Supreme Court and High Courts',
                  'Cross-referenced statutory laws and acts',
                  'Plain English & Urdu case summaries',
                  'Secure client-lawyer portal'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300 font-medium">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
                      <Scale className="w-3.5 h-3.5" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <StatBox value="15k+" label="Indexed Judgments" />
              <StatBox value="200+" label="Public Laws" className="mt-8" />
              <StatBox value="24/7" label="AI Legal Research" className="-mt-8" />
              <StatBox value="100%" label="Secure & Private" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-black mb-6">Ready to transform your practice?</h2>
          <p className="text-emerald-100 text-lg mb-10 max-w-2xl mx-auto">
            Join thousands of legal professionals and citizens using PakLawSite to navigate the complexities of Pakistani law with confidence.
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={onGetStarted}
              className="px-8 py-4 bg-white text-emerald-900 hover:bg-slate-50 font-bold rounded-2xl shadow-xl transition-all active:scale-95 text-lg"
            >
              Start Searching Now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
